import { RotateCw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CopyAudienceProblems from './CopyAudienceProblems';
import CreateTemplateBody from './CreateTemplateBody';
import { CreateTemplateProvider } from './CreateTemplateContext';
import { useCreateTemplateForm } from './hooks/useCreateTemplateForm';
import { useCreateTemplateSubmit } from './hooks/useCreateTemplateSubmit';
import { useDestinationAccessGroups } from './hooks/useDestinationAccessGroups';
import type { TCreateTemplateForm } from './types';
import {
  canShareTemplateWithOrg,
  getTemplateCreateAccessGroupIds,
  getTemplateCreateAccessGroupOptions,
} from 'utils/templateAccessGroups';
import { isHubertAdminUser } from 'types/user.types';
import { useAppDispatch } from 'rtk/query/hooks';
import { toggleCreateTemplateDialog } from 'rtk/layout/LayoutControlSlice';
import { useUser } from 'hooks/useUser';
import { HubertButton } from 'components/HubertUI/HubertButton';
import MutedText from 'components/common/MutedText';
import type { TVisibility } from 'types/template/template.types';
import type { TTemplateAudienceProblem } from 'types/template/templateAudience.types';

interface CreateTemplateProps {
  initialTemplate?: Partial<TCreateTemplateForm>;
}

const CreateTemplate = ({ initialTemplate }: CreateTemplateProps) => {
  const { t } = useTranslation('Templates');
  const dispatch = useAppDispatch();
  const user = useUser();
  const isHubertAdmin = isHubertAdminUser(user);
  const canShareWithOrg = canShareTemplateWithOrg(user);

  const initialAccessGroupOptions = getTemplateCreateAccessGroupOptions(user);

  const result = useCreateTemplateForm({
    isHubertAdmin,
    userClientId: isHubertAdmin ? '' : user.client_id,
    canShareWithOrg,
    templateCreateAccessGroupIds: getTemplateCreateAccessGroupIds(user),
    initialTemplate,
  });

  const accessGroupOptions = useDestinationAccessGroups({
    destinationClientId: result.newTemplate.destinationClientId,
    initialAccessGroupOptions,
    skip: !isHubertAdmin,
  });

  const { onCreateTemplate, isLoading } = useCreateTemplateSubmit({
    newTemplate: result.newTemplate,
    isHubertAdmin,
  });

  const [rejectedCopy, setRejectedCopy] = useState<{
    problems: TTemplateAudienceProblem[];
    baseTemplateId?: string;
    visibility: TVisibility;
    accessGroupIds: string[];
  }>();
  const currentAccessGroupIds = result.newTemplate.accessGroupIds ?? [];
  const copyProblemsStillApply =
    rejectedCopy &&
    rejectedCopy.baseTemplateId === result.newTemplate.baseTemplateId &&
    rejectedCopy.visibility === result.newTemplate.visibility &&
    rejectedCopy.accessGroupIds.length === currentAccessGroupIds.length &&
    rejectedCopy.accessGroupIds.every((id) => currentAccessGroupIds.includes(id));

  const missingRequiredFields = [
    !result.newTemplate.name.trim().length ? t('createTemplate.fieldName') : null,
    result.requiresDestinationClient && !result.newTemplate.destinationClientId
      ? t('createTemplate.fieldDestinationClient')
      : null,
    result.isCopyMode && !result.newTemplate.baseTemplateId ? t('createTemplate.fieldBaseTemplate') : null,
    result.isCopyMode && result.newTemplate.sourceScope === 'other' && !result.newTemplate.sourceClientId
      ? t('createTemplate.fieldSourceClient')
      : null,
  ].filter(Boolean) as string[];

  const state = {
    isHubertAdmin,
    accessGroupOptions,
    form: result,
  };

  return (
    <div>
      <div className="mb-6">
        <div className="text-xl font-semibold tracking-tight">{t('createTemplate.title')}</div>
        <MutedText text={t('createTemplate.subtitle')} />
      </div>

      <CreateTemplateProvider state={state}>
        <CreateTemplateBody />
      </CreateTemplateProvider>

      <div className="bottom-0 mt-6 flex w-full flex-col items-end">
        <div className="flex items-center justify-end gap-3">
          <HubertButton
            variant="secondary"
            size="sm"
            onClick={() => {
              dispatch(toggleCreateTemplateDialog({ show: false, data: {} }));
            }}
          >
            {t('createTemplate.cancelButton')}
          </HubertButton>

          <HubertButton
            disabled={result.isSubmitDisabled}
            size="sm"
            onClick={async () => {
              const created = await onCreateTemplate();
              setRejectedCopy(
                created.audienceProblems
                  ? {
                      problems: created.audienceProblems,
                      baseTemplateId: result.newTemplate.baseTemplateId,
                      visibility: result.newTemplate.visibility,
                      accessGroupIds: currentAccessGroupIds,
                    }
                  : undefined,
              );
            }}
          >
            {isLoading ? <RotateCw className="mr-2 animate-spin" /> : null}
            {t('createTemplate.createButton')}
          </HubertButton>
        </div>
        {copyProblemsStillApply ? (
          <CopyAudienceProblems
            problems={rejectedCopy.problems}
            accessGroupIds={currentAccessGroupIds}
            onProblemsChange={(problems) => setRejectedCopy({ ...rejectedCopy, problems })}
            copyVisibility={rejectedCopy.visibility}
          />
        ) : null}
        {result.isSubmitDisabled && missingRequiredFields.length ? (
          <div className="mt-3 inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800 shadow-xs">
            <span className="tracking-wide text-amber-700 uppercase">{t('createTemplate.missingRequiredLabel')}</span>
            <span className="h-3 w-px bg-amber-200" aria-hidden="true" />
            <span className="font-medium text-amber-900 normal-case">{missingRequiredFields.join(', ')}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CreateTemplate;
